import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Stars } from './ui.jsx';
import Icon from './Icon.jsx';
import NoAvailabilityDialog from './NoAvailabilityDialog.jsx';
import { currency, csvList } from '../lib/format.js';
import ServiceIcon from './ServiceIcon.jsx';

/**
 * The search result card. Reading order is deliberate: who they are, how well
 * they are rated, what they cost, what they do — the four things that decide a
 * booking — with the two actions pinned to the bottom so a column of cards has
 * its buttons on one line.
 */
export default function NurseCard({ nurse, index = 0 }) {
  const languages = csvList(nurse.languages);
  const services = nurse.services || [];
  const [blocked, setBlocked] = useState(false);

  // The server decides bookability (published, future, unbooked windows). Older
  // payloads without the flag fall back to bookable so the card never blocks a
  // nurse it has no information about.
  const bookable = nurse.is_bookable !== false;

  return (
    <article className="nurse-card anim-rise" style={{ '--i': index }}>
      <div className="nurse-card-top">
        <Avatar name={nurse.name} avatar={nurse.avatar} />
        <div className="grow">
          <div className="nurse-name">
            <span className="truncate">{nurse.name}</span>
            <span className="verified-tick" title="Verified nurse">
              <Icon name="shield-check" title="Verified nurse" />
            </span>
          </div>
          <div className="nurse-headline">{nurse.headline || nurse.bio}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="rate">{currency(nurse.hourly_rate)}</div>
          <small className="rate-unit">per hour</small>
        </div>
      </div>

      <div className="nurse-meta">
        <Stars value={nurse.rating_avg} count={nurse.rating_count} />
        <span title="Experience">
          <Icon name="briefcase" />
          {nurse.experience_years} yr{nurse.experience_years === 1 ? '' : 's'}
        </span>
        <span title="Location" className="truncate">
          <Icon name="map-pin" />
          {nurse.city}
          {nurse.distance_km != null && ` · ${nurse.distance_km} km`}
        </span>
        {nurse.jobs_completed > 0 && (
          <span title="Completed visits">
            <Icon name="check-circle" />
            {nurse.jobs_completed} visits
          </span>
        )}
      </div>

      {services.length > 0 && (
        <div className="chips">
          {services.slice(0, 3).map((s) => (
            <span key={s.id} className="chip chip-static">
              <ServiceIcon service={s} size={14} /> {s.name}
            </span>
          ))}
          {services.length > 3 && <span className="chip chip-static">+{services.length - 3} more</span>}
        </div>
      )}

      {languages.length > 0 && (
        <div className="row-tight tiny dim">
          <Icon name="languages" size={14} />
          <span className="truncate">Speaks {languages.join(', ')}</span>
        </div>
      )}

      {!bookable && (
        <span className="pill pill-warning" style={{ alignSelf: 'flex-start' }}>
          <Icon name="calendar" /> No open slots
        </span>
      )}

      <div className="row" style={{ marginTop: 'auto', gap: 'var(--s-2)', flexWrap: 'nowrap' }}>
        <Link to={`/nurses/${nurse.id}`} className="btn btn-sm grow">
          View profile
        </Link>
        {bookable ? (
          <Link to={`/book/${nurse.id}`} className="btn btn-sm btn-primary grow">
            Book now
          </Link>
        ) : (
          // Still a real, focusable control — it explains itself rather than
          // being disabled, which would leave the patient guessing why.
          <button type="button" className="btn btn-sm btn-primary grow" onClick={() => setBlocked(true)}>
            Book now
          </button>
        )}
      </div>

      {blocked && <NoAvailabilityDialog nurse={nurse} onClose={() => setBlocked(false)} />}
    </article>
  );
}
